import React, { useRef, useState, useCallback } from 'react';

/**
 * Interpolate automation value at a given beat time.
 */
export function getAutomationValue(nodes, time, defaultValue = 1) {
  if (!nodes || nodes.length === 0) return defaultValue;
  if (nodes.length === 1) return nodes[0].value;

  const sorted = [...nodes].sort((a, b) => a.time - b.time);

  if (time <= sorted[0].time) return sorted[0].value;
  if (time >= sorted[sorted.length - 1].time) return sorted[sorted.length - 1].value;

  let lo = sorted[0], hi = sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].time <= time && sorted[i + 1].time >= time) {
      lo = sorted[i];
      hi = sorted[i + 1];
      break;
    }
  }

  const t = (time - lo.time) / (hi.time - lo.time);
  return lo.value + t * (hi.value - lo.value);
}

const LANE_HEIGHT = 72;

const PARAM_META = {
  volume: {
    label: 'Volume',
    min: 0, max: 1,
    defaultValue: 0.8,
    color: '#facc15',       // yellow — classic DAW volume color
    fillColor: 'rgba(250,204,21,0.12)',
    format: v => `${Math.round(v * 100)}%`,
  },
  pan: {
    label: 'Pan',
    min: -1, max: 1,
    defaultValue: 0,
    color: '#22d3ee',       // cyan — pan
    fillColor: 'rgba(34,211,238,0.10)',
    format: v => v === 0 ? 'C' : v > 0 ? `R${Math.round(v * 100)}` : `L${Math.round(Math.abs(v) * 100)}`,
  },
};

// Mode display config
const MODE_CONFIG = {
  read: { label: 'READ', bg: '#15803d', border: '#22c55e', text: '#dcfce7' },
  write: { label: 'WRITE', bg: '#b91c1c', border: '#ef4444', text: '#fee2e2' },
};

function valueToY(value, meta) {
  const { min, max } = meta;
  return LANE_HEIGHT - ((value - min) / (max - min)) * LANE_HEIGHT;
}

function yToValue(y, meta) {
  const { min, max } = meta;
  const clamped = Math.max(0, Math.min(LANE_HEIGHT, y));
  return min + (1 - clamped / LANE_HEIGHT) * (max - min);
}

export default function AutomationLane({
  trackId,
  param,
  nodes,
  pixelsPerBeat,
  timelineWidth,
  trackLaneGridCSS,
  currentTime,
  automationWriteMode,
  onNodesChange,
}) {
  const meta = PARAM_META[param] || PARAM_META.volume;
  const modeConfig = MODE_CONFIG[automationWriteMode] || MODE_CONFIG.read;
  const svgRef = useRef(null);
  const draggingNodeRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const sorted = [...nodes].sort((a, b) => a.time - b.time);

  // Build the SVG path: extends to edges, connects all nodes
  const buildPath = () => {
    if (sorted.length === 0) {
      const y = valueToY(meta.defaultValue, meta);
      return `M0,${y} L${timelineWidth},${y}`;
    }
    const pts = sorted.map(n => ({ x: n.time * pixelsPerBeat, y: valueToY(n.value, meta) }));
    let d = `M0,${pts[0].y} L${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      d += ` L${pts[i + 1].x},${pts[i + 1].y}`;
    }
    d += ` L${timelineWidth},${pts[pts.length - 1].y}`;
    return d;
  };

  const buildFillPath = () => {
    const linePath = buildPath();
    return `${linePath} L${timelineWidth},${LANE_HEIGHT} L0,${LANE_HEIGHT} Z`;
  };

  const getTimeFromClientX = useCallback((clientX) => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    return Math.max(0, (clientX - rect.left) / pixelsPerBeat);
  }, [pixelsPerBeat]);

  const getValueFromClientY = useCallback((clientY) => {
    if (!svgRef.current) return meta.defaultValue;
    const rect = svgRef.current.getBoundingClientRect();
    return yToValue(clientY - rect.top, meta);
  }, [meta]);

  const handleSVGClick = (e) => {
    if (e.target.dataset.nodeIndex !== undefined) return;
    if (automationWriteMode === 'read') return;
    const time = getTimeFromClientX(e.clientX);
    const value = getValueFromClientY(e.clientY);
    const snappedTime = Math.round(time * 4) / 4;
    const newNodes = [
      ...nodes.filter(n => Math.abs(n.time - snappedTime) > 0.01),
      { time: snappedTime, value },
    ].sort((a, b) => a.time - b.time);
    onNodesChange(newNodes);
  };

  const handleNodeMouseDown = (e, sortedIndex) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (automationWriteMode === 'read') return;

    const node = sorted[sortedIndex];
    draggingNodeRef.current = {
      nodeRef: node,
      origTime: node.time,
      origValue: node.value,
      startX: e.clientX,
      startY: e.clientY,
    };

    const onMove = (ev) => {
      if (!draggingNodeRef.current) return;
      const { nodeRef, origTime, origValue, startX, startY } = draggingNodeRef.current;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const newTime = Math.max(0, origTime + dx / pixelsPerBeat);
      const newValue = Math.max(meta.min, Math.min(meta.max,
        origValue - (dy / LANE_HEIGHT) * (meta.max - meta.min)
      ));
      onNodesChange(
        nodes.map(n => n === nodeRef ? { time: newTime, value: newValue } : n)
          .sort((a, b) => a.time - b.time)
      );
    };

    const onUp = () => {
      draggingNodeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleNodeContextMenu = (e, sortedIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, nodeIndex: sortedIndex });
  };

  const currentValue = getAutomationValue(nodes, currentTime, meta.defaultValue);
  const playheadX = currentTime * pixelsPerBeat;
  const canEdit = automationWriteMode === 'write';
  const isActive = true; // lane is always active (no 'off' mode)

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: `${LANE_HEIGHT + 4}px`,
      backgroundColor: '#070c14',
      borderTop: `2px solid ${modeConfig.border}`,
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      {/* SVG — the actual curve canvas */}
      <svg
        ref={svgRef}
        width={timelineWidth}
        height={LANE_HEIGHT}
        style={{
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          cursor: !isActive ? 'default' : canEdit ? 'crosshair' : 'default',
          backgroundImage: trackLaneGridCSS?.backgroundImage,
          backgroundSize: trackLaneGridCSS?.backgroundSize,
        }}
        onClick={handleSVGClick}
      >
        {/* Faint mid-line (100% for volume, center for pan) */}
        <line
          x1={0} y1={valueToY(meta.defaultValue, meta)}
          x2={timelineWidth} y2={valueToY(meta.defaultValue, meta)}
          stroke={meta.color}
          strokeWidth={0.5}
          strokeDasharray="6 4"
          opacity={0.3}
          style={{ pointerEvents: 'none' }}
        />

        {/* Fill under curve */}
        <path
          d={buildFillPath()}
          fill={meta.fillColor}
          style={{ pointerEvents: 'none' }}
        />

        {/* Automation curve line */}
        <path
          d={buildPath()}
          fill="none"
          stroke={isActive ? meta.color : '#334155'}
          strokeWidth={isActive ? 2 : 1}
          style={{ pointerEvents: 'none' }}
        />

        {/* Nodes */}
        {isActive && sorted.map((n, i) => (
          <circle
            key={i}
            data-node-index={i}
            cx={n.time * pixelsPerBeat}
            cy={valueToY(n.value, meta)}
            r={hoveredIndex === i ? 7 : 5}
            fill={hoveredIndex === i ? 'white' : meta.color}
            stroke={meta.color}
            strokeWidth={2}
            style={{ cursor: canEdit ? 'grab' : 'default', transition: 'r 0.1s' }}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
            onMouseDown={(e) => handleNodeMouseDown(e, i)}
            onContextMenu={(e) => handleNodeContextMenu(e, i)}
          />
        ))}

        {/* Playhead */}
        <line
          x1={playheadX} y1={0}
          x2={playheadX} y2={LANE_HEIGHT}
          stroke="#facc15"
          strokeWidth={1}
          opacity={0.5}
          style={{ pointerEvents: 'none' }}
        />
        {/* Value indicator on playhead */}
        <circle
          cx={playheadX}
          cy={valueToY(currentValue, meta)}
          r={3}
          fill="#facc15"
          opacity={0.9}
          style={{ pointerEvents: 'none' }}
        />
      </svg>

      {/* Floating label: param name + live value */}
      <div style={{
        position: 'absolute',
        top: 3,
        left: 6,
        fontSize: '10px',
        fontWeight: '600',
        color: isActive ? meta.color : '#475569',
        pointerEvents: 'none',
        userSelect: 'none',
        textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        zIndex: 2,
      }}>
        {meta.label}
        <span style={{ fontWeight: 'normal', marginLeft: 4, color: '#cbd5e1', opacity: isActive ? 1 : 0.5 }}>
          {meta.format(currentValue)}
        </span>
      </div>

      {/* "click to add nodes" hint when empty and in write mode */}
      {canEdit && nodes.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '10px',
          color: '#475569',
          pointerEvents: 'none',
          userSelect: 'none',
          marginTop: '2px',
        }}>
          Click to add automation points
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onMouseDown={() => setContextMenu(null)} />
          <div style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '6px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            minWidth: '140px',
            overflow: 'hidden',
            fontSize: '13px',
          }}>
            <div
              style={{ padding: '8px 14px', cursor: 'pointer', color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              onMouseDown={e => {
                e.stopPropagation();
                const nodeToDelete = sorted[contextMenu.nodeIndex];
                onNodesChange(nodes.filter(n => n !== nodeToDelete));
                setContextMenu(null);
              }}
            >
              Delete Node
            </div>
          </div>
        </>
      )}
    </div>
  );
}
